import { useEffect, useState } from 'react'

import type { GridSelection } from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'

export interface FormatBarAnchor {
  align: 'center'
  top: number
  left: number
}

const FORMAT_BAR_GAP_PX = 12
const FORMAT_BAR_SIDE_MARGIN_PX = 16
const FORMAT_BAR_SAFE_WIDTH_PX = 320

const rectFromRects = (rects: DOMRect[]) => {
  if (rects.length === 0) return null

  const left = Math.min(...rects.map((rect) => rect.left))
  const right = Math.max(...rects.map((rect) => rect.right))
  const top = Math.min(...rects.map((rect) => rect.top))
  const bottom = Math.max(...rects.map((rect) => rect.bottom))

  return new DOMRect(left, top, right - left, bottom - top)
}

const interactiveColumnCount = (gridRoot: HTMLDivElement) => {
  return gridRoot.querySelectorAll('[data-time-grid-column-index]').length
}

const getSelectionRect = ({
  selection,
  gridRoot,
}: {
  selection: GridSelection
  gridRoot: HTMLDivElement
}): DOMRect | null => {
  if (selection.kind === 'none' || selection.kind === 'all' || selection.kind === 'event')
    return null

  if (selection.kind === 'cells') {
    const overlayRects = Array.from(selection.keys)
      .map((key) =>
        gridRoot.querySelector<HTMLElement>(
          `[data-time-grid-selection-overlay-key="${CSS.escape(key)}"]`
        )
      )
      .filter((node): node is HTMLElement => Boolean(node))
      .map((node) => node.getBoundingClientRect())

    return rectFromRects(overlayRects)
  }

  if (selection.kind === 'row') {
    const row = gridRoot.querySelector<HTMLElement>(
      `[data-time-grid-row-id="${CSS.escape(selection.rowId)}"]`
    )
    const firstColumn = gridRoot.querySelector<HTMLElement>('[data-time-grid-column-index="0"]')
    const lastColumn = gridRoot.querySelector<HTMLElement>(
      `[data-time-grid-column-index="${interactiveColumnCount(gridRoot) - 1}"]`
    )

    if (!row || !firstColumn || !lastColumn) return null

    const rowRect = row.getBoundingClientRect()
    const firstColumnRect = firstColumn.getBoundingClientRect()
    const lastColumnRect = lastColumn.getBoundingClientRect()

    return new DOMRect(
      firstColumnRect.left,
      rowRect.top,
      lastColumnRect.right - firstColumnRect.left,
      rowRect.height
    )
  }

  if (selection.kind === 'rowRange') {
    const rowRects = Array.from(selection.keys)
      .map((key) =>
        gridRoot.querySelector<HTMLElement>(`[data-time-grid-row-id="${CSS.escape(key)}"]`)
      )
      .filter((node): node is HTMLElement => Boolean(node))
      .map((node) => node.getBoundingClientRect())
    const firstColumn = gridRoot.querySelector<HTMLElement>('[data-time-grid-column-index="0"]')
    const lastColumn = gridRoot.querySelector<HTMLElement>(
      `[data-time-grid-column-index="${interactiveColumnCount(gridRoot) - 1}"]`
    )
    const rowsRect = rectFromRects(rowRects)

    if (!rowsRect || !firstColumn || !lastColumn) return null

    const firstColumnRect = firstColumn.getBoundingClientRect()
    const lastColumnRect = lastColumn.getBoundingClientRect()

    return new DOMRect(
      firstColumnRect.left,
      rowsRect.top,
      lastColumnRect.right - firstColumnRect.left,
      rowsRect.height
    )
  }

  if (selection.kind === 'column') {
    const startColumn = gridRoot.querySelector<HTMLElement>(
      `[data-time-grid-column-index="${selection.columnIndex}"]`
    )
    const endColumn = gridRoot.querySelector<HTMLElement>(
      `[data-time-grid-column-index="${selection.columnIndex}"]`
    )

    if (!startColumn || !endColumn) return null

    const startColumnRect = startColumn.getBoundingClientRect()
    const endColumnRect = endColumn.getBoundingClientRect()

    return new DOMRect(
      startColumnRect.left,
      startColumnRect.top,
      endColumnRect.right - startColumnRect.left,
      startColumnRect.height
    )
  }

  if (selection.kind === 'columnRange') {
    const columnRects = Array.from(selection.keys)
      .map((key) => gridRoot.querySelector<HTMLElement>(`[data-time-grid-column-index="${key}"]`))
      .filter((node): node is HTMLElement => Boolean(node))
      .map((node) => node.getBoundingClientRect())

    return rectFromRects(columnRects)
  }

  return null
}

const resolveFormatBarAnchor = ({
  selectionRect,
  viewportWidth,
}: {
  selectionRect: DOMRect
  viewportWidth: number
}): FormatBarAnchor => {
  const centeredLeft = selectionRect.left + selectionRect.width / 2
  const estimatedHalfWidth = FORMAT_BAR_SAFE_WIDTH_PX / 2
  const top = selectionRect.top - FORMAT_BAR_GAP_PX
  const minLeft = FORMAT_BAR_SIDE_MARGIN_PX + estimatedHalfWidth
  const maxLeft = viewportWidth - FORMAT_BAR_SIDE_MARGIN_PX - estimatedHalfWidth
  const left = Math.max(minLeft, Math.min(centeredLeft, maxLeft))

  return {
    align: 'center',
    top,
    left,
  }
}

const useFormatBarAnchor = ({
  visible,
  selection,
  gridRoot,
}: {
  visible: boolean
  selection: GridSelection
  gridRoot: HTMLDivElement | null
}) => {
  const [anchor, setAnchor] = useState<FormatBarAnchor | null>(null)

  useEffect(() => {
    if (!visible || !gridRoot) {
      setAnchor(null)
      return
    }

    const rect = getSelectionRect({ selection, gridRoot })
    if (!rect) {
      setAnchor(null)
      return
    }

    setAnchor(resolveFormatBarAnchor({ selectionRect: rect, viewportWidth: window.innerWidth }))
  }, [gridRoot, selection, visible])

  return anchor
}

export { useFormatBarAnchor }
